import React, { useState, useEffect } from 'react';
import { api } from '../api/client.ts';
import { useGame } from '../state/gameStore.tsx';
import {
  REFERRAL_MILESTONES,
  type DailyRewardsStatus,
  type BoostInventoryItem,
  type DailyQuest,
  type BoostUsageLog,
  type ReferralStatusResponse,
} from '@galaxy/shared';
import { telegramHaptics } from '../telegram/sdk.ts';

interface DailyRewardsAndBoostsViewProps {
  onOpenShop?: () => void;
  initialSubTab?: 'daily' | 'inventory' | 'quests' | 'referral' | 'logs';
}

export const DailyRewardsAndBoostsView: React.FC<DailyRewardsAndBoostsViewProps> = ({
  onOpenShop,
  initialSubTab = 'daily',
}) => {
  const { state, refreshState, setNotification } = useGame();
  const [subTab, setSubTab] = useState<'daily' | 'inventory' | 'quests' | 'referral' | 'logs'>(initialSubTab);

  const [dailyStatus, setDailyStatus] = useState<DailyRewardsStatus | null>(null);
  const [inventory, setInventory] = useState<BoostInventoryItem[]>([]);
  const [quests, setQuests] = useState<DailyQuest[]>([]);
  const [logs, setLogs] = useState<BoostUsageLog[]>([]);
  const [referralStatus, setReferralStatus] = useState<ReferralStatusResponse | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Quick Apply Modal State
  const [selectedItem, setSelectedItem] = useState<BoostInventoryItem | null>(null);
  const [selectedTargetType, setSelectedTargetType] = useState<'building' | 'research' | 'shipyard' | 'active_buff'>('building');
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');

  const loadData = async () => {
    try {
      setLoading(true);
      const [rewRes, invRes, qstRes, logRes, refRes] = await Promise.all([
        api.getDailyRewards().catch(() => null),
        api.getBoostInventory().catch(() => null),
        api.getQuests().catch(() => null),
        api.getBoostUsageLogs().catch(() => null),
        api.getReferralStatus().catch(() => null),
      ]);

      if (rewRes?.success) setDailyStatus(rewRes.status);
      if (invRes?.success) setInventory(invRes.items);
      if (qstRes?.success) setQuests(qstRes.quests);
      if (logRes?.success) setLogs(logRes.logs);
      if (refRes?.success) setReferralStatus(refRes);
    } catch (err: any) {
      console.error('Error loading boost and referral data:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle claiming daily reward
  const handleClaimDaily = async () => {
    try {
      setActionLoading(true);
      telegramHaptics.impact('heavy');
      const res = await api.claimDailyReward();
      if (res.success) {
        setDailyStatus(res.status);
        telegramHaptics.notification('success');
        setNotification(`🎁 Đã nhận phần thưởng Ngày ${res.streak}: ${res.reward.name}!`);
        await loadData();
        await refreshState();
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle claiming quest reward
  const handleClaimQuest = async (questId: string) => {
    try {
      setActionLoading(true);
      telegramHaptics.impact('medium');
      const res = await api.claimQuest(questId);
      if (res.success) {
        telegramHaptics.notification('success');
        setNotification('📜 Đã nhận phần thưởng nhiệm vụ vào kho tăng tốc!');
        setQuests(res.quests);
        setInventory(res.inventory);
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle consuming boost item
  const handleConsumeBoost = async () => {
    if (!selectedItem || !selectedTargetId) {
      setNotification('⚠️ Vui lòng chọn mục tiêu áp dụng tăng tốc!');
      return;
    }

    try {
      setActionLoading(true);
      telegramHaptics.impact('heavy');
      const res = await api.consumeBoost(selectedItem.id, selectedTargetType, selectedTargetId);
      if (res.success) {
        telegramHaptics.notification('success');
        const minutesSaved = Math.round(res.time_saved_ms / 60000);
        setNotification(`⚡ Đã tăng tốc thành công! Tiết kiệm ${minutesSaved} phút.`);
        setInventory(res.inventory);
        setSelectedItem(null);
        await refreshState();
        await loadData();
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle copy referral link
  const handleCopyReferralLink = async () => {
    const link = referralStatus?.referral_link;
    if (!link) {
      setNotification('⏳ Đang tạo liên kết mời, vui lòng thử lại sau ít giây!');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      telegramHaptics.notification('success');
      setCopiedLink(true);
      setNotification('📋 Đã sao chép liên kết mời bạn thành công!');
      setTimeout(() => setCopiedLink(false), 3000);
    } catch {
      setNotification('📋 Hãy sao chép liên kết từ hộp văn bản!');
    }
  };

  // Handle share referral link to Telegram
  const handleShareReferral = () => {
    const link = referralStatus?.referral_link;
    if (!link) {
      setNotification('⏳ Đang tạo liên kết mời, vui lòng thử lại sau ít giây!');
      return;
    }
    const shareUrl = referralStatus?.share_url || `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent('🚀 Tham gia GalaxyTelegram cùng tôi! Nâng cấp căn cứ và nhận quà tân thủ ngay!')}`;
    telegramHaptics.impact('medium');
    const tg = window.Telegram?.WebApp;
    if (tg && typeof (tg as any).openTelegramLink === 'function') {
      (tg as any).openTelegramLink(shareUrl);
    } else {
      window.open(shareUrl, '_blank');
    }
  };

  // Handle claiming milestone reward
  const handleClaimMilestone = async (milestone: number) => {
    try {
      setActionLoading(true);
      telegramHaptics.impact('heavy');
      const res = await api.claimReferralMilestone(milestone);
      if (res.success) {
        telegramHaptics.notification('success');
        setNotification(`🎉 ${res.message}`);
        await loadData();
        await refreshState();
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle complete tutorial
  const handleCompleteTutorial = async () => {
    try {
      setActionLoading(true);
      telegramHaptics.impact('medium');
      const res = await api.completeTutorial();
      if (res.success) {
        telegramHaptics.notification('success');
        setNotification('🚀 Đã xác nhận hoàn thành hướng dẫn tân thủ!');
        await loadData();
        await refreshState();
      }
    } catch (err: any) {
      telegramHaptics.notification('error');
      setNotification(`⚠️ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // List of active candidate queues for boosting
  const activeQueues: Array<{
    type: 'building' | 'research' | 'shipyard' | 'active_buff';
    id: string;
    label: string;
    finishAt: number;
  }> = [];

  if (state) {
    const now = Date.now();
    // 1. Buildings upgrading
    (state.buildings || []).forEach((b) => {
      if (b.upgrade_finish_at && b.upgrade_finish_at > now) {
        activeQueues.push({
          type: 'building',
          id: `${state.planet?.id || ''}:${b.type}`,
          label: `Công trình: ${b.type.toUpperCase()} (Lv.${b.level} ➔ ${b.level + 1})`,
          finishAt: b.upgrade_finish_at,
        });
      }
    });

    // 2. Researches in progress
    (state.research || []).forEach((r) => {
      if (r.finish_at && r.finish_at > now) {
        activeQueues.push({
          type: 'research',
          id: r.tech_id,
          label: `Nghiên cứu: ${r.tech_id.toUpperCase()} (Lv.${r.level} ➔ ${r.level + 1})`,
          finishAt: r.finish_at,
        });
      }
    });

    // 3. Ship production queues
    (state.shipyard_queue || []).forEach((q) => {
      if (q.finish_at && q.finish_at > now) {
        activeQueues.push({
          type: 'shipyard',
          id: q.id,
          label: `Xưởng tàu: ${q.design_name || 'Chiến hạm'} (${q.count} tàu)`,
          finishAt: q.finish_at,
        });
      }
    });
  }

  return (
    <div style={{ padding: '12px 14px', maxWidth: 700, margin: '0 auto' }}>
      {/* View Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14,
        borderBottom: '1px solid var(--border-panel)',
        paddingBottom: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 24 }}>🎁</span>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 16,
              letterSpacing: 1,
              color: 'var(--accent-cyan)',
              fontWeight: 800,
            }}>
              PHẦN THƯỞNG & KHO TĂNG TỐC
            </h2>
            <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
              Điểm danh hằng ngày, nhận quà miễn phí và rút ngắn thời gian chỉ huy
            </div>
          </div>
        </div>

        {onOpenShop && (
          <button
            className="sci-btn"
            onClick={onOpenShop}
            style={{
              fontSize: 11,
              padding: '6px 12px',
              background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(202, 138, 4, 0.3) 100%)',
              borderColor: '#fde047',
              color: '#fde047',
            }}
          >
            ⭐ MUA STARS
          </button>
        )}
      </div>

      {/* Sub Navigation Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 5,
        marginBottom: 16,
      }}>
        {[
          { id: 'daily', label: 'ĐIỂM DANH', icon: '🎁' },
          { id: 'inventory', label: 'KHO TỐC', icon: '⚡' },
          { id: 'quests', label: 'NHIỆM VỤ', icon: '📜' },
          { id: 'referral', label: 'MỜI BẠN', icon: '👥' },
          { id: 'logs', label: 'LỊCH SỬ', icon: '🕒' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              telegramHaptics.selection();
              setSubTab(tab.id as any);
            }}
            style={{
              padding: '8px 2px',
              borderRadius: 4,
              border: subTab === tab.id ? '1px solid var(--accent-cyan)' : '1px solid var(--border-panel)',
              background: subTab === tab.id ? 'rgba(0, 229, 255, 0.15)' : 'rgba(10, 17, 36, 0.6)',
              color: subTab === tab.id ? 'var(--accent-cyan)' : 'var(--text-dim)',
              fontSize: 10,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
          ĐANG TẢI DỮ LIỆU TĂNG TỐC...
        </div>
      ) : (
        <>
          {/* TAB 1: 7-DAY REWARD CALENDAR */}
          {subTab === 'daily' && dailyStatus && (
            <div>
              {/* Streak Banner */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.05) 100%)',
                border: '1px solid #10b981',
                borderRadius: 6,
                padding: '12px 14px',
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 11, color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    CHUỖI ĐĂNG NHẬP LIÊN TỤC
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#ffffff', marginTop: 2 }}>
                    🔥 {dailyStatus.current_streak} / 7 NGÀY
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                    {dailyStatus.can_claim_today
                      ? 'Bạn có phần thưởng chưa nhận hôm nay!'
                      : 'Đã nhận thưởng hôm nay. Phần thưởng tiếp theo lúc 00:00 UTC.'}
                  </div>
                </div>

                <button
                  className="sci-btn"
                  disabled={!dailyStatus.can_claim_today || actionLoading}
                  onClick={handleClaimDaily}
                  style={{
                    padding: '10px 18px',
                    fontSize: 12,
                    fontWeight: 800,
                    background: dailyStatus.can_claim_today
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'rgba(55, 65, 81, 0.5)',
                    borderColor: dailyStatus.can_claim_today ? '#34d399' : '#4b5563',
                    color: dailyStatus.can_claim_today ? '#ffffff' : '#9ca3af',
                    boxShadow: dailyStatus.can_claim_today ? '0 0 15px rgba(16, 185, 129, 0.5)' : undefined,
                  }}
                >
                  {dailyStatus.can_claim_today ? '🎁 NHẬN THƯỞNG' : '✓ ĐÃ NHẬN'}
                </button>
              </div>

              {/* 7-Day Visual Cards Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))',
                gap: 8,
                marginBottom: 16,
              }}>
                {dailyStatus.schedule.map((item) => {
                  const isCurrent = item.is_today;
                  const isClaimed = item.claimed;

                  return (
                    <div
                      key={item.day}
                      style={{
                        background: isCurrent
                          ? 'rgba(0, 229, 255, 0.12)'
                          : isClaimed
                          ? 'rgba(16, 185, 129, 0.08)'
                          : 'rgba(10, 17, 36, 0.5)',
                        border: isCurrent
                          ? '2px solid var(--accent-cyan)'
                          : isClaimed
                          ? '1px solid #10b981'
                          : '1px solid var(--border-panel)',
                        borderRadius: 6,
                        padding: '10px 6px',
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: isCurrent ? '0 0 12px rgba(0, 229, 255, 0.3)' : undefined,
                      }}
                    >
                      <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: isCurrent ? 'var(--accent-cyan)' : isClaimed ? '#10b981' : 'var(--text-dim)',
                        marginBottom: 4,
                      }}>
                        NGÀY {item.day}
                      </div>

                      <div style={{ fontSize: 24, margin: '4px 0' }}>
                        {item.day === 7 ? '👑' : item.minutes >= 30 ? '⚡' : '⏱️'}
                      </div>

                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-bright)' }}>
                        +{item.minutes}p
                      </div>

                      <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>
                        {item.quantity}x Thẻ
                      </div>

                      {isClaimed && (
                        <div style={{
                          marginTop: 6,
                          fontSize: 10,
                          fontWeight: 800,
                          color: '#10b981',
                        }}>
                          ✓ ĐÃ LẤY
                        </div>
                      )}
                      {isCurrent && !isClaimed && (
                        <div style={{
                          marginTop: 6,
                          fontSize: 10,
                          fontWeight: 800,
                          color: 'var(--accent-cyan)',
                          animation: 'pulse 1.5s infinite',
                        }}>
                          ★ HÔM NAY
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Tomorrow Reward Teaser */}
              <div style={{
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px dashed var(--border-panel)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 12,
                color: 'var(--text-dim)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span>🔮</span>
                <span>
                  Phần thưởng ngày mai:{' '}
                  <strong style={{ color: 'var(--accent-cyan)' }}>
                    {dailyStatus.next_reward.name} ({dailyStatus.next_reward.quantity}x thẻ +{dailyStatus.next_reward.minutes} phút)
                  </strong>. Đừng quên đăng nhập để duy trì chuỗi thưởng!
                </span>
              </div>
            </div>
          )}

          {subTab === 'daily' && !dailyStatus && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-dim)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🎁</div>
              <div style={{ fontSize: 14, color: '#f87171', marginBottom: 16 }}>
                Chưa thể tải dữ liệu điểm danh hàng ngày.
              </div>
              <button
                className="sci-btn"
                onClick={loadData}
                style={{
                  padding: '8px 20px',
                  fontSize: 12,
                  fontWeight: 700,
                  background: 'rgba(0, 229, 255, 0.2)',
                  borderColor: 'var(--accent-cyan)',
                  color: '#ffffff',
                }}
              >
                🔄 TẢI LẠI DỮ LIỆU
              </button>
            </div>
          )}

          {/* TAB 2: BOOST INVENTORY */}
          {subTab === 'inventory' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  Tổng số loại vật phẩm trong kho: <strong style={{ color: 'var(--text-bright)' }}>{inventory.length}</strong>
                </span>
                {onOpenShop && (
                  <button
                    className="sci-btn"
                    onClick={onOpenShop}
                    style={{ fontSize: 10, padding: '4px 10px' }}
                  >
                    + THÊM TĂNG TỐC TỪ SHOP
                  </button>
                )}
              </div>

              {inventory.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 16px',
                  background: 'rgba(10, 17, 36, 0.4)',
                  borderRadius: 6,
                  border: '1px dashed var(--border-panel)',
                  color: 'var(--text-dim)',
                  fontSize: 13,
                }}>
                  Kho tăng tốc hiện đang trống! Hãy điểm danh nhận thẻ miễn phí hoặc xem Cửa Hàng Stars.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {inventory.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        background: 'rgba(10, 17, 36, 0.7)',
                        border: '1px solid var(--border-panel)',
                        borderRadius: 6,
                        padding: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 800,
                            padding: '2px 6px',
                            borderRadius: 3,
                            background: item.item_type === 'free' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                            color: item.item_type === 'free' ? '#10b981' : '#fde047',
                            border: item.item_type === 'free' ? '1px solid #10b981' : '1px solid #fde047',
                          }}>
                            {item.item_type === 'free' ? 'MIỄN PHÍ' : 'STARS'}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-bright)' }}>
                            {item.name}
                          </span>
                          <span style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: 'var(--accent-cyan)',
                            background: 'rgba(0, 229, 255, 0.1)',
                            padding: '1px 6px',
                            borderRadius: 10,
                          }}>
                            x{item.quantity}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.4 }}>
                          {item.description}
                        </div>
                      </div>

                      <button
                        className="sci-btn"
                        onClick={() => {
                          telegramHaptics.impact('light');
                          setSelectedItem(item);
                          if (item.target_type === 'building' || item.target_type === 'all') setSelectedTargetType('building');
                          else if (item.target_type === 'research') setSelectedTargetType('research');
                          else if (item.target_type === 'shipyard') setSelectedTargetType('shipyard');
                          if (activeQueues.length > 0) setSelectedTargetId(activeQueues[0].id);
                        }}
                        style={{
                          fontSize: 11,
                          padding: '8px 14px',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ⚡ DÙNG NGAY
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: DAILY & WEEKLY QUESTS */}
          {subTab === 'quests' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {quests.map((q) => (
                <div
                  key={q.id}
                  style={{
                    background: 'rgba(10, 17, 36, 0.7)',
                    border: '1px solid var(--border-panel)',
                    borderRadius: 6,
                    padding: 12,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 800,
                        padding: '2px 6px',
                        borderRadius: 3,
                        background: q.type === 'daily' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                        color: q.type === 'daily' ? '#60a5fa' : '#c084fc',
                        border: q.type === 'daily' ? '1px solid #3b82f6' : '1px solid #a855f7',
                      }}>
                        {q.type === 'daily' ? 'NHIỆM VỤ NGÀY' : 'NHIỆM VỤ TUẦN'}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-bright)' }}>
                        {q.title}
                      </span>
                    </div>

                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6 }}>
                      {q.description}
                    </div>

                    <div style={{ fontSize: 11, color: 'var(--accent-cyan)' }}>
                      🎁 Phần thưởng: <strong>{q.reward_name} (x{q.reward_quantity})</strong>
                    </div>
                  </div>

                  <button
                    className="sci-btn"
                    disabled={!q.completed || q.claimed || actionLoading}
                    onClick={() => handleClaimQuest(q.id)}
                    style={{
                      fontSize: 11,
                      padding: '8px 14px',
                      fontWeight: 700,
                      background: q.claimed
                        ? 'transparent'
                        : q.completed
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'rgba(55, 65, 81, 0.5)',
                      borderColor: q.claimed ? '#4b5563' : q.completed ? '#34d399' : '#4b5563',
                      color: q.claimed ? '#9ca3af' : q.completed ? '#ffffff' : '#9ca3af',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {q.claimed ? '✓ ĐÃ NHẬN' : q.completed ? 'NHẬN QUÀ' : 'CHƯA ĐẠT'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* TAB: REFERRAL REWARDS (MỜI BẠN) */}
          {subTab === 'referral' && (
            <div>
              {/* Header Box */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(10, 17, 36, 0.8) 100%)',
                border: '1px solid var(--accent-cyan)',
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 16,
                boxShadow: '0 0 15px rgba(0, 229, 255, 0.1)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>👥</span>
                      <span>MỜI BẠN NHẬN THƯỞNG CHIẾN HẠM & TĂNG TỐC</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4, maxWidth: 480 }}>
                      Chia sẻ liên kết chỉ huy của bạn. Nhận tăng tốc miễn phí, skin tàu Tiên Phong Vanguard và khung đại diện Vương Miện Thiên Hà!
                    </div>
                  </div>

                  {referralStatus && !referralStatus.tutorial_completed && (
                    <button
                      className="sci-btn"
                      onClick={handleCompleteTutorial}
                      disabled={actionLoading}
                      style={{
                        fontSize: 10,
                        padding: '6px 10px',
                        background: 'rgba(234, 179, 8, 0.2)',
                        borderColor: '#fde047',
                        color: '#fde047',
                      }}
                    >
                      🚀 HOÀN THÀNH HƯỚNG DẪN
                    </button>
                  )}
                </div>

                {/* Referral Link & Actions */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6, fontWeight: 700 }}>
                    LIÊN KẾT MỜI CHỈ HUY CỦA BẠN:
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(5, 8, 17, 0.8)',
                    border: '1px solid var(--border-panel)',
                    borderRadius: 6,
                    padding: '8px 10px',
                  }}>
                    <input
                      readOnly
                      value={referralStatus?.referral_link || 'Đang tạo liên kết mời bảo mật...'}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--accent-cyan)',
                        fontSize: 11,
                        fontFamily: 'monospace',
                        outline: 'none',
                      }}
                    />
                    <button
                      className="sci-btn"
                      onClick={handleCopyReferralLink}
                      style={{
                        fontSize: 11,
                        padding: '6px 12px',
                        background: copiedLink ? '#10b981' : 'rgba(0, 229, 255, 0.2)',
                        borderColor: copiedLink ? '#10b981' : 'var(--accent-cyan)',
                        color: '#ffffff',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {copiedLink ? '✓ ĐÃ CHÉP' : '📋 SAO CHÉP'}
                    </button>
                    <button
                      className="sci-btn"
                      onClick={handleShareReferral}
                      style={{
                        fontSize: 11,
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        borderColor: '#38bdf8',
                        color: '#ffffff',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      🚀 CHIA SẺ
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Overview */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 16,
              }}>
                <div style={{
                  background: 'rgba(10, 17, 36, 0.7)',
                  border: '1px solid var(--border-panel)',
                  borderRadius: 6,
                  padding: '12px 8px',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700 }}>TỔNG ĐÃ MỜI</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--accent-cyan)', marginTop: 4 }}>
                    {referralStatus?.total_invited ?? 0}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>chỉ huy kết nối</div>
                </div>

                <div style={{
                  background: 'rgba(10, 17, 36, 0.7)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  borderRadius: 6,
                  padding: '12px 8px',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, color: '#34d399', fontWeight: 700 }}>HỢP LỆ (TÍNH THƯỞNG)</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#10b981', marginTop: 4 }}>
                    {referralStatus?.qualified_count ?? 0}
                  </div>
                  <div style={{ fontSize: 9, color: '#34d399', marginTop: 2 }}>đủ điều kiện mốc</div>
                </div>

                <div style={{
                  background: 'rgba(10, 17, 36, 0.7)',
                  border: '1px solid rgba(234, 179, 8, 0.4)',
                  borderRadius: 6,
                  padding: '12px 8px',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 10, color: '#fde047', fontWeight: 700 }}>ĐANG CHỜ TIẾN ĐỘ</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#eab308', marginTop: 4 }}>
                    {referralStatus?.pending_count ?? 0}
                  </div>
                  <div style={{ fontSize: 9, color: '#fde047', marginTop: 2 }}>chưa đủ 3 tiêu chí</div>
                </div>
              </div>

              {/* Strict Qualification Rules Banner */}
              <div style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid var(--border-panel)',
                borderRadius: 6,
                padding: '10px 14px',
                marginBottom: 16,
                fontSize: 11,
              }}>
                <div style={{ fontWeight: 800, color: 'var(--text-bright)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🛡️</span>
                  <span>TIÊU CHUẨN XÁC THỰC LỜI MỜI HỢP LỆ (CHỐNG GIAN LẬN):</span>
                </div>
                <div style={{ color: 'var(--text-dim)', lineHeight: 1.5 }}>
                  Để đảm bảo sự công bằng và chống tài khoản clone, 1 lượt mời chỉ được tính hợp lệ khi tân thủ:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8 }}>
                  <div style={{ background: 'rgba(5, 8, 17, 0.6)', padding: '6px 8px', borderRadius: 4, borderLeft: '3px solid var(--accent-cyan)' }}>
                    <div style={{ color: 'var(--text-bright)', fontWeight: 700, fontSize: 10 }}>1. Hướng Dẫn</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 9 }}>Hoàn thành hướng dẫn khởi đầu</div>
                  </div>
                  <div style={{ background: 'rgba(5, 8, 17, 0.6)', padding: '6px 8px', borderRadius: 4, borderLeft: '3px solid #10b981' }}>
                    <div style={{ color: 'var(--text-bright)', fontWeight: 700, fontSize: 10 }}>2. Căn Cứ Cấp 3</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 9 }}>Nâng Trung Tâm Chỉ Huy ≥ Cấp 3</div>
                  </div>
                  <div style={{ background: 'rgba(5, 8, 17, 0.6)', padding: '6px 8px', borderRadius: 4, borderLeft: '3px solid #eab308' }}>
                    <div style={{ color: 'var(--text-bright)', fontWeight: 700, fontSize: 10 }}>3. Ngày Tiếp Theo</div>
                    <div style={{ color: 'var(--text-dim)', fontSize: 9 }}>Đăng nhập quay lại vào ngày sau</div>
                  </div>
                </div>
              </div>

              {/* Milestones Track */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--accent-cyan)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🏆</span>
                  <span>CÁC MỐC PHẦN THƯỞNG MỜI CHỈ HUY</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {((referralStatus?.milestones && referralStatus.milestones.length > 0)
                    ? referralStatus.milestones
                    : REFERRAL_MILESTONES.map((def) => ({
                        milestone: def.milestone,
                        reward_type: def.reward_type,
                        reward_id: def.reward_id,
                        reward_quantity: def.reward_quantity,
                        reward_name: def.reward_name,
                        description: def.description,
                        claimed: false,
                        can_claim: false,
                      }))
                  ).map((m) => {
                    const currentQualified = referralStatus?.qualified_count ?? 0;
                    const progressPercent = Math.min(100, Math.round((currentQualified / m.milestone) * 100));
                    const isCosmetic = m.reward_type === 'ship_skin' || m.reward_type === 'galaxy_deco';

                    return (
                      <div
                        key={m.milestone}
                        style={{
                          background: m.claimed
                            ? 'rgba(10, 17, 36, 0.4)'
                            : m.can_claim
                            ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(10, 17, 36, 0.7) 100%)'
                            : 'rgba(10, 17, 36, 0.6)',
                          border: m.claimed
                            ? '1px solid #374151'
                            : m.can_claim
                            ? '1px solid var(--accent-cyan)'
                            : '1px solid var(--border-panel)',
                          borderRadius: 8,
                          padding: '12px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          boxShadow: m.can_claim ? '0 0 15px rgba(0, 229, 255, 0.2)' : 'none',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18 }}>
                              {m.reward_type === 'ship_skin' ? '🛡️' : m.reward_type === 'galaxy_deco' ? '👑' : '⚡'}
                            </span>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-bright)' }}>
                                  MỐC {m.milestone} BẠN: {m.reward_name}
                                </span>
                                {isCosmetic && (
                                  <span style={{
                                    fontSize: 9,
                                    padding: '1px 6px',
                                    borderRadius: 3,
                                    background: m.reward_type === 'ship_skin' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                                    color: m.reward_type === 'ship_skin' ? '#34d399' : '#38bdf8',
                                    border: `1px solid ${m.reward_type === 'ship_skin' ? '#10b981' : '#38bdf8'}`,
                                    fontWeight: 700,
                                  }}>
                                    ĐỘC QUYỀN
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                                {m.description}
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div style={{ marginTop: 8, maxWidth: 360 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)', marginBottom: 3 }}>
                              <span>Tiến độ:</span>
                              <span style={{ fontWeight: 700, color: currentQualified >= m.milestone ? '#10b981' : 'var(--text-bright)' }}>
                                {currentQualified}/{m.milestone} Bạn ({progressPercent}%)
                              </span>
                            </div>
                            <div style={{ height: 5, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{
                                width: `${progressPercent}%`,
                                height: '100%',
                                background: m.claimed ? '#6b7280' : 'linear-gradient(90deg, var(--accent-cyan) 0%, #10b981 100%)',
                                transition: 'width 0.3s ease',
                              }} />
                            </div>
                          </div>
                        </div>

                        {/* Claim Button */}
                        <div>
                          {m.claimed ? (
                            <button
                              disabled
                              style={{
                                padding: '8px 14px',
                                fontSize: 11,
                                fontWeight: 700,
                                background: 'transparent',
                                border: '1px solid #4b5563',
                                color: '#9ca3af',
                                borderRadius: 4,
                                cursor: 'not-allowed',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ✓ ĐÃ NHẬN
                            </button>
                          ) : m.can_claim ? (
                            <button
                              className="sci-btn"
                              disabled={actionLoading}
                              onClick={() => handleClaimMilestone(m.milestone)}
                              style={{
                                padding: '8px 16px',
                                fontSize: 11,
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                borderColor: '#34d399',
                                color: '#ffffff',
                                boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              🎁 NHẬN THƯỞNG
                            </button>
                          ) : (
                            <button
                              disabled
                              style={{
                                padding: '8px 14px',
                                fontSize: 10,
                                fontWeight: 700,
                                background: 'rgba(55, 65, 81, 0.3)',
                                border: '1px solid #4b5563',
                                color: '#9ca3af',
                                borderRadius: 4,
                                cursor: 'not-allowed',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              🔒 CHƯA ĐỦ
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Invited Referees List */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-bright)', marginBottom: 8 }}>
                  DANH SÁCH CHỈ HUY ĐÃ MỜI ({referralStatus?.referrals?.length ?? 0})
                </div>

                {(!referralStatus?.referrals || referralStatus.referrals.length === 0) ? (
                  <div style={{
                    textAlign: 'center',
                    padding: 28,
                    color: 'var(--text-dim)',
                    border: '1px dashed var(--border-panel)',
                    borderRadius: 6,
                    fontSize: 12,
                  }}>
                    Chưa có chỉ huy nào tham gia qua liên kết của bạn. Hãy sao chép hoặc chia sẻ liên kết ở trên để mời đồng đội ngay!
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {referralStatus.referrals.map((ref) => (
                      <div
                        key={ref.id}
                        style={{
                          background: 'rgba(10, 17, 36, 0.6)',
                          border: ref.status === 'qualified' ? '1px solid rgba(16, 185, 129, 0.4)' : '1px solid var(--border-panel)',
                          borderRadius: 6,
                          padding: '10px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 11,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--text-bright)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🚀 {ref.referee_name}</span>
                            <span style={{
                              fontSize: 9,
                              padding: '1px 5px',
                              borderRadius: 3,
                              background: ref.status === 'qualified' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                              color: ref.status === 'qualified' ? '#34d399' : '#fde047',
                              fontWeight: 700,
                            }}>
                              {ref.status === 'qualified' ? '✓ HỢP LỆ' : '⏳ ĐANG CHỜ'}
                            </span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                            Tham gia: {new Date(ref.created_at).toLocaleDateString('vi-VN')} {new Date(ref.created_at).toLocaleTimeString('vi-VN')}
                          </div>
                        </div>

                        {/* Progress checklist */}
                        <div style={{ display: 'flex', gap: 6, fontSize: 9 }}>
                          <span style={{
                            padding: '2px 5px',
                            borderRadius: 3,
                            background: ref.progress.tutorial ? 'rgba(16, 185, 129, 0.15)' : 'rgba(75, 85, 99, 0.2)',
                            color: ref.progress.tutorial ? '#34d399' : '#9ca3af',
                          }}>
                            {ref.progress.tutorial ? '✓' : '○'} Hướng dẫn
                          </span>
                          <span style={{
                            padding: '2px 5px',
                            borderRadius: 3,
                            background: ref.progress.command_center_lvl3 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(75, 85, 99, 0.2)',
                            color: ref.progress.command_center_lvl3 ? '#34d399' : '#9ca3af',
                          }}>
                            {ref.progress.command_center_lvl3 ? '✓' : '○'} Căn cứ Lv.3
                          </span>
                          <span style={{
                            padding: '2px 5px',
                            borderRadius: 3,
                            background: ref.progress.returned_next_day ? 'rgba(16, 185, 129, 0.15)' : 'rgba(75, 85, 99, 0.2)',
                            color: ref.progress.returned_next_day ? '#34d399' : '#9ca3af',
                          }}>
                            {ref.progress.returned_next_day ? '✓' : '○'} Ngày 2+
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: USAGE LOGS */}
          {subTab === 'logs' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 10 }}>
                Nhật ký các lần sử dụng vật phẩm tăng tốc gần đây:
              </div>

              {logs.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: 30,
                  color: 'var(--text-dim)',
                  border: '1px dashed var(--border-panel)',
                  borderRadius: 6,
                }}>
                  Chưa có lịch sử sử dụng tăng tốc nào được ghi nhận.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      style={{
                        background: 'rgba(10, 17, 36, 0.6)',
                        border: '1px solid var(--border-panel)',
                        borderRadius: 4,
                        padding: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: 11,
                      }}
                    >
                      <div>
                        <div style={{ color: 'var(--text-bright)', fontWeight: 700 }}>
                          ⚡ {log.item_id.toUpperCase()} ➔ {log.target_type.toUpperCase()}
                        </div>
                        <div style={{ color: 'var(--text-dim)', fontSize: 10, marginTop: 2 }}>
                          {new Date(log.used_at).toLocaleTimeString('vi-VN')} {new Date(log.used_at).toLocaleDateString('vi-VN')}
                        </div>
                      </div>

                      <div style={{ color: '#10b981', fontWeight: 800, fontSize: 12 }}>
                        -{Math.round(log.time_saved_ms / 60000)} phút
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* QUICK APPLY MODAL */}
      {selectedItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          backdropFilter: 'blur(6px)',
        }}>
          <div style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--accent-cyan)',
            borderRadius: 8,
            padding: 18,
            width: '100%',
            maxWidth: 440,
            boxShadow: '0 0 25px rgba(0, 229, 255, 0.3)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
              borderBottom: '1px solid var(--border-panel)',
              paddingBottom: 8,
            }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--accent-cyan)' }}>
                ⚡ XÁC NHẬN SỬ DỤNG TĂNG TỐC
              </span>
              <button
                onClick={() => setSelectedItem(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-bright)' }}>
                {selectedItem.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                {selectedItem.description}
              </div>
            </div>

            {/* Target Queue Selector */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, color: 'var(--text-dim)', display: 'block', marginBottom: 4 }}>
                CHỌN TIẾN TRÌNH ÁP DỤNG:
              </label>

              {activeQueues.length === 0 ? (
                <div style={{
                  padding: 12,
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 4,
                  fontSize: 11,
                  color: '#f87171',
                }}>
                  Không có công trình, nghiên cứu hoặc lô tàu nào đang trong tiến trình nâng cấp/đóng tàu!
                </div>
              ) : (
                <select
                  value={selectedTargetId}
                  onChange={(e) => {
                    setSelectedTargetId(e.target.value);
                    const q = activeQueues.find((item) => item.id === e.target.value);
                    if (q) setSelectedTargetType(q.type);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    background: 'rgba(10, 17, 36, 0.9)',
                    border: '1px solid var(--border-panel)',
                    color: 'var(--text-bright)',
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: 'inherit',
                  }}
                >
                  {activeQueues.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.label} (Còn ~{Math.max(0, Math.round((q.finishAt - Date.now()) / 60000))} phút)
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="sci-btn"
                onClick={() => setSelectedItem(null)}
                style={{ padding: '8px 14px', fontSize: 11, background: 'transparent' }}
              >
                HỦY
              </button>
              <button
                className="sci-btn"
                disabled={activeQueues.length === 0 || !selectedTargetId || actionLoading}
                onClick={handleConsumeBoost}
                style={{
                  padding: '8px 16px',
                  fontSize: 11,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, var(--accent-cyan) 0%, #0284c7 100%)',
                  color: '#050811',
                }}
              >
                {actionLoading ? 'ĐANG XỬ LÝ...' : 'XÁC NHẬN TIÊU THỤ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
